import React, { useState, memo } from 'react';
import { Button } from '../components/ui/Button';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { HeartIcon, UsersIcon, VideoIcon, CheckIcon, ArrowRightIcon, SparklesIcon, GlobeIcon, Camera, Bell, Share2Icon, ClipboardListIcon, PlayCircleIcon, CalendarIcon, MessageSquareIcon, CopyIcon, ChevronDownIcon, ChevronUpIcon, MapPinIcon, BusIcon, ShirtIcon, BuildingIcon, MegaphoneIcon, ImageIcon, BarChart3Icon, StarIcon, ClockIcon, MapIcon, AlertCircleIcon, SunIcon, SmileIcon, BookOpenIcon, PhoneIcon, MessageCircleIcon, HelpCircleIcon, BellRingIcon } from 'lucide-react';
export function DestaPass() {
  const [expandedScript, setExpandedScript] = useState<string | null>('couple');
  const [copiedScript, setCopiedScript] = useState<string | null>(null);
  const [activeJob, setActiveJob] = useState(0);
  const calendlyUrl = 'https://calendar.app.google/MukVwRkZJ1qD6Px27';
  const features = [{
    icon: <UsersIcon className="h-6 w-6" data-id="element-1782" />,
    title: 'Pre-Wedding Social Network',
    description: 'Connect both families months before the big day through chat, stories, and icebreakers.'
  }, {
    icon: <GlobeIcon className="h-6 w-6" data-id="element-1783" />,
    title: 'Destination & Cultural Guide',
    description: 'Help every guest feel informed about your destination and wedding traditions.'
  }, {
    icon: <Share2Icon className="h-6 w-6" data-id="element-1784" />,
    title: 'Live Social Sharing',
    description: 'Capture and share moments in real-time so remote family never misses a thing.'
  }, {
    icon: <Camera className="h-6 w-6" data-id="element-1785" />,
    title: 'Shared Photo Album',
    description: 'One beautiful collaborative album for all wedding photos and videos.'
  }, {
    icon: <Bell className="h-6 w-6" data-id="element-1786" />,
    title: 'Smart Logistics',
    description: 'Event schedules, transportation, and real-time updates for all guests.'
  }, {
    icon: <SparklesIcon className="h-6 w-6" data-id="element-1787" />,
    title: 'Quests & Games',
    description: 'Fun challenges and icebreakers to help guests connect before they meet.'
  }];
  const preparationChecklist = [{
    title: 'Couple Welcome Video',
    description: 'A 30-60 second video from both of you welcoming guests to your wedding app',
    required: true
  }, {
    title: "Bride's Parents Welcome Video",
    description: "A short video from the bride's parents welcoming the groom's family",
    required: true
  }, {
    title: "Groom's Parents Welcome Video",
    description: "A short video from the groom's parents welcoming the bride's family",
    required: true
  }, {
    title: 'Wedding Event Schedule',
    description: "List of events with dates, times, and dress codes (only if you don't have a wedding planner—we can get this from them)",
    required: false
  }, {
    title: 'Guest List with Emails',
    description: 'Can include people who may not attend in person—we can also get this from your planner if needed',
    required: false
  }, {
    title: 'Engagement Photos',
    description: 'A few photos to populate the initial gallery',
    required: false
  }];
  const scripts = {
    couple: {
      title: 'Couple Welcome Video Script',
      duration: '30-60 seconds',
      options: [{
        name: 'Warm & Personal',
        script: `Hi everyone! We're [Name] and [Name], and we are SO excited that you're joining us for our wedding!

We created this app so you can connect with everyone coming to celebrate with us—whether you're from [City] or [City], we want you to feel like family before you even arrive.

You'll find the event schedule, destination guides, and a place to share photos and get to know other guests. 

We can't wait to celebrate with you in [Destination]! See you soon!`
      }, {
        name: 'Fun & Casual',
        script: `Hey! It's [Name] and [Name]! 

So we're getting MARRIED and you're invited! 🎉

We made this app because let's be honest—our families haven't met yet and we want to change that BEFORE the wedding chaos begins.

Use it to check out the schedule, learn about [Destination], and maybe make some new friends along the way. There might even be some fun challenges involved...

Can't wait to party with all of you! Let's gooo!`
      }, {
        name: 'Traditional & Elegant',
        script: `Namaste and welcome to our wedding celebration.

We are [Name] and [Name], and we are deeply honored that you will be joining us as we begin our journey together.

This app will serve as your guide to our wedding festivities in [Destination]. Here you will find event details, cultural guides to help you understand our traditions, and a way to connect with other guests.

We look forward to celebrating this joyous occasion with you. Thank you for being part of our story.`
      }]
    },
    brideParents: {
      title: "Bride's Parents Welcome Video Script",
      duration: '20-40 seconds',
      options: [{
        name: 'Warm Welcome',
        script: `Hello! We are [Name] and [Name], [Bride's name]'s parents.

We are thrilled to welcome [Groom's name] and his wonderful family into ours. 

To [Groom's family name] family—we can't wait to meet all of you and celebrate together in [Destination]. Please don't hesitate to reach out through this app if you have any questions.

See you soon!`
      }, {
        name: 'Heartfelt & Traditional',
        script: `Namaste! We are [Bride's name]'s parents, [Name] and [Name].

It brings us immense joy to welcome [Groom's name] and the entire [Family name] family. Our daughter has found a wonderful partner, and we are blessed to be joining our families together.

We look forward to meeting each of you and celebrating this beautiful union. Please use this app to connect with us and other guests.

With love and warm regards.`
      }]
    },
    groomParents: {
      title: "Groom's Parents Welcome Video Script",
      duration: '20-40 seconds',
      options: [{
        name: 'Warm Welcome',
        script: `Hello everyone! We are [Name] and [Name], [Groom's name]'s parents.

We are so happy to welcome [Bride's name] into our family—she is truly a blessing.

To [Bride's family name] family—thank you for raising such a wonderful daughter. We can't wait to meet all of you in [Destination] and celebrate together.

See you at the wedding!`
      }, {
        name: 'Heartfelt & Traditional',
        script: `Namaste! We are [Groom's name]'s parents, [Name] and [Name].

We feel truly blessed to welcome [Bride's name] and the entire [Family name] family into our lives. Our son has found his perfect match.

We eagerly await the opportunity to meet each of you and celebrate this auspicious occasion together. Please reach out through this app—we would love to connect before the festivities begin.

With warmest regards.`
      }]
    }
  };
  const copyToClipboard = (text: string, scriptId: string) => {
    navigator.clipboard.writeText(text);
    setCopiedScript(scriptId);
    setTimeout(() => setCopiedScript(null), 2000);
  };
  return <div className="bg-gradient-to-b from-purple-50 via-white to-pink-50 min-h-screen" data-id="element-1788">
      {/* Decorative Pattern */}
      <div className="fixed inset-0 pointer-events-none opacity-5 z-0" data-id="element-1789">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-1790">
          <defs data-id="element-1791">
            <pattern id="paisley" x="0" y="0" width="150" height="150" patternUnits="userSpaceOnUse" data-id="element-1792">
              <path d="M75 10 Q 90 30, 85 50 Q 80 70, 75 75 Q 70 70, 65 50 Q 60 30, 75 10 Z" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-1793" />
              <circle cx="75" cy="45" r="8" fill="none" stroke="currentColor" strokeWidth="1" data-id="element-1794" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#paisley)" className="text-purple-600" data-id="element-1795" />
        </svg>
      </div>

      {/* Hero - Congratulations */}
      <section className="relative pt-32 pb-20 overflow-hidden" data-id="element-1796">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-600 via-pink-600 to-purple-700" data-id="element-1797"></div>
        <div className="absolute inset-0 opacity-20" data-id="element-1798">
          <div className="absolute top-20 left-10 w-64 h-64 bg-white rounded-full blur-3xl" data-id="element-1799"></div>
          <div className="absolute bottom-20 right-10 w-80 h-80 bg-pink-300 rounded-full blur-3xl" data-id="element-1800"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-1801">
          <div className="max-w-3xl mx-auto text-center text-white" data-id="element-1802">
            <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium mb-6 border border-white/30" data-id="element-1803">
              <HeartIcon className="h-4 w-4 mr-2 text-pink-300" data-id="element-1804" />
              Included with Your DestaWed Package
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight" data-id="element-1805">
              Congratulations! 🎉
            </h1>
            <p className="text-2xl md:text-3xl font-medium mb-4 text-white/95" data-id="element-1806">
              Your DestaPass is Ready to Activate
            </p>
            <p className="text-xl text-white/80 mb-8 leading-relaxed max-w-2xl mx-auto" data-id="element-1807">
              DestaPass is your private wedding app where both families can
              connect, share, and celebrate together—starting right now.
            </p>

            <a href={calendlyUrl} target="_blank" rel="noopener noreferrer" data-id="element-1808">
              <Button variant="primary" size="lg" className="!bg-gradient-to-r !from-pink-500 !to-purple-500 !text-white hover:!from-pink-600 hover:!to-purple-600 border-0 shadow-xl font-semibold" data-id="element-1809">
                Schedule Your Activation Call
                <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-1810" />
              </Button>
            </a>

            <p className="text-sm text-white/70 mt-4" data-id="element-1811">
              30-minute call • We'll record your welcome videos together
            </p>
          </div>
        </div>
      </section>

      {/* See DestaPass in Action - Video Section */}
      <section className="py-16 md:py-20 bg-white relative z-10" data-id="element-1812">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1813">
          <div className="max-w-5xl mx-auto" data-id="element-1814">
            <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-1815">
              {/* Video */}
              <div className="flex justify-center order-2 lg:order-1" data-id="element-1816">
                <div className="relative" data-id="element-1817">
                  {/* Phone frame */}
                  <div className="relative bg-gray-900 rounded-[2.5rem] p-2 shadow-2xl" data-id="element-1818">
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-24 h-6 bg-gray-900 rounded-b-xl z-10" data-id="element-1819"></div>
                    <div className="relative rounded-[2rem] overflow-hidden bg-black" style={{
                    width: '260px',
                    height: '462px'
                  }} data-id="element-1820">
                      <iframe src="https://www.youtube.com/embed/6QTyeeFyK2A?autoplay=0&loop=1&playlist=6QTyeeFyK2A&controls=1&modestbranding=1&rel=0" title="See DestaPass in Action" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-1821"></iframe>
                    </div>
                  </div>
                  {/* Decorative glow */}
                  <div className="absolute -top-4 -right-4 w-20 h-20 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full opacity-40 blur-xl" data-id="element-1822"></div>
                  <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-gradient-to-br from-pink-400 to-purple-400 rounded-full opacity-40 blur-xl" data-id="element-1823"></div>
                </div>
              </div>

              {/* Content */}
              <div className="order-1 lg:order-2" data-id="element-1824">
                <div className="inline-flex items-center px-4 py-2 bg-purple-100 rounded-full text-purple-700 text-sm font-medium mb-4" data-id="element-1825">
                  <PlayCircleIcon className="h-4 w-4 mr-2" data-id="element-1826" />
                  See It in Action
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-1827">
                  What is DestaPass?
                </h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed" data-id="element-1828">
                  Watch this quick video to see how DestaPass brings your
                  wedding guests together before the big day—connecting
                  families, sharing memories, and building excitement.
                </p>
                <div className="space-y-4" data-id="element-1829">
                  <div className="flex items-start gap-3" data-id="element-1830">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-1831">
                      <UsersIcon className="h-4 w-4 text-purple-600" data-id="element-1832" />
                    </div>
                    <div data-id="element-1833">
                      <h4 className="font-semibold text-gray-900" data-id="element-1834">
                        Connect Both Families
                      </h4>
                      <p className="text-sm text-gray-600" data-id="element-1835">
                        Guests from both sides get to know each other before
                        arriving
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-1836">
                    <div className="w-8 h-8 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-1837">
                      <Camera className="h-4 w-4 text-pink-600" data-id="element-1838" />
                    </div>
                    <div data-id="element-1839">
                      <h4 className="font-semibold text-gray-900" data-id="element-1840">
                        Share Every Moment
                      </h4>
                      <p className="text-sm text-gray-600" data-id="element-1841">
                        One place for all photos, videos, and memories
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-1842">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-1843">
                      <Bell className="h-4 w-4 text-purple-600" data-id="element-1844" />
                    </div>
                    <div data-id="element-1845">
                      <h4 className="font-semibold text-gray-900" data-id="element-1846">
                        Stay Informed
                      </h4>
                      <p className="text-sm text-gray-600" data-id="element-1847">
                        Event schedules, logistics, and real-time updates
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Your Guests Need This — 5 Core Jobs */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white via-purple-50/30 to-purple-50/50 relative z-10 overflow-hidden" data-id="element-1848">
        {/* Decorative background elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-pink-200/20 to-transparent rounded-full blur-3xl" data-id="element-1849"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-purple-200/20 to-transparent rounded-full blur-3xl" data-id="element-1850"></div>

        <div className="container mx-auto px-4 md:px-6 relative" data-id="element-1851">
          <div className="max-w-5xl mx-auto" data-id="element-1852">
            <div className="text-center mb-14" data-id="element-1853">
              <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full text-purple-700 text-sm font-medium mb-5 border border-purple-200/50" data-id="element-1854">
                <SparklesIcon className="h-4 w-4 mr-2" data-id="element-1855" />
                The Real Value
              </div>
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4" data-id="element-1856">
                What DestaPass Actually Solves
              </h2>
              <p className="text-lg text-gray-500 max-w-xl mx-auto" data-id="element-1857">
                Think of it as{' '}
                <span className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600" data-id="element-1858">
                  Google Maps + Calendar + Concierge
                </span>{' '}
                — built for your wedding.
              </p>
            </div>

            {/* Job Selector Tabs */}
            <div className="flex justify-center overflow-x-auto gap-2 pb-4 mb-8 scrollbar-hide" data-id="element-1859">
              {[{
              icon: <CalendarIcon className="h-4 w-4" data-id="element-1860" />,
              label: 'Schedule',
              emoji: '📅'
            }, {
              icon: <BusIcon className="h-4 w-4" data-id="element-1861" />,
              label: 'Transport',
              emoji: '🚌'
            }, {
              icon: <ShirtIcon className="h-4 w-4" data-id="element-1862" />,
              label: 'Attire & Culture',
              emoji: '👗'
            }, {
              icon: <BuildingIcon className="h-4 w-4" data-id="element-1863" />,
              label: 'On-Site Info',
              emoji: '🏨'
            }, {
              icon: <MegaphoneIcon className="h-4 w-4" data-id="element-1864" />,
              label: 'Communication',
              emoji: '📣'
            }].map((tab, idx) => <button key={idx} onClick={() => setActiveJob(idx)} className={`flex items-center gap-2 px-5 py-3 rounded-2xl text-sm font-semibold whitespace-nowrap transition-all duration-300 ${activeJob === idx ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/25 scale-105' : 'bg-white text-gray-600 border border-gray-200 hover:border-purple-300 hover:text-purple-700 hover:shadow-md'}`} data-id="element-1865">
                  <span className="text-base" data-id="element-1866">{tab.emoji}</span>
                  {tab.label}
                </button>)}
            </div>

            {/* Job Content Cards */}
            {activeJob === 0 && <div className="bg-white rounded-3xl border border-purple-100/80 shadow-xl shadow-purple-500/5 overflow-hidden" data-id="element-1867">
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6 md:p-8" data-id="element-1868">
                  <div className="flex items-start gap-4" data-id="element-1869">
                    <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center text-white font-bold text-xl flex-shrink-0 border border-white/30" data-id="element-1870">
                      1
                    </div>
                    <div data-id="element-1871">
                      <h3 className="text-xl md:text-2xl font-bold text-white" data-id="element-1872">
                        "Where do I need to be, and when?"
                      </h3>
                      <p className="text-sm text-white/80 mt-1" data-id="element-1873">
                        The #1 reason guests open the app. Indian weddings have
                        overlapping events and flexible timing — this removes
                        all confusion.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-6 md:p-8" data-id="element-1874">
                  <div className="grid sm:grid-cols-2 gap-4" data-id="element-1875">
                    <div className="group bg-gradient-to-br from-purple-50 to-pink-50/50 rounded-2xl p-5 border border-purple-100/50 hover:shadow-md transition-all duration-300" data-id="element-1876">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white mb-3 group-hover:scale-110 transition-transform" data-id="element-1877">
                        <CalendarIcon className="h-5 w-5" data-id="element-1878" />
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm mb-1.5" data-id="element-1879">
                        Event Timeline
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-1880">
                        Full schedule by day — Mehndi, Sangeet, Ceremony,
                        Reception — with start times, "arrive by" buffers, and
                        venue pins.
                      </p>
                    </div>
                    <div className="group bg-gradient-to-br from-purple-50 to-pink-50/50 rounded-2xl p-5 border border-purple-100/50 hover:shadow-md transition-all duration-300" data-id="element-1881">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white mb-3 group-hover:scale-110 transition-transform" data-id="element-1882">
                        <ShirtIcon className="h-5 w-5" data-id="element-1883" />
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm mb-1.5" data-id="element-1884">
                        Dress Code Per Event
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-1885">
                        Clear attire guidance on every event card so guests
                        never show up in the wrong outfit.
                      </p>
                    </div>
                    <div className="group bg-gradient-to-br from-purple-50 to-pink-50/50 rounded-2xl p-5 border border-purple-100/50 hover:shadow-md transition-all duration-300" data-id="element-1886">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white mb-3 group-hover:scale-110 transition-transform" data-id="element-1887">
                        <BellRingIcon className="h-5 w-5" data-id="element-1888" />
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm mb-1.5" data-id="element-1889">
                        Smart Reminders
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-1890">
                        "Mehndi starts in 90 mins" and "Buses leaving in 30
                        mins" — push notifications that keep everyone on track.
                      </p>
                    </div>
                    <div className="group bg-gradient-to-br from-purple-50 to-pink-50/50 rounded-2xl p-5 border border-purple-100/50 hover:shadow-md transition-all duration-300" data-id="element-1891">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white mb-3 group-hover:scale-110 transition-transform" data-id="element-1892">
                        <MapIcon className="h-5 w-5" data-id="element-1893" />
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm mb-1.5" data-id="element-1894">
                        Venue Maps
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-1895">
                        Map pins and directions for every event location — no
                        more "which ballroom?" confusion.
                      </p>
                    </div>
                  </div>
                </div>
              </div>}

            {activeJob === 1 && <div className="bg-white rounded-3xl border border-purple-100/80 shadow-xl shadow-purple-500/5 overflow-hidden" data-id="element-1896">
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6 md:p-8" data-id="element-1897">
                  <div className="flex items-start gap-4" data-id="element-1898">
                    <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center text-white font-bold text-xl flex-shrink-0 border border-white/30" data-id="element-1899">
                      2
                    </div>
                    <div data-id="element-1900">
                      <h3 className="text-xl md:text-2xl font-bold text-white" data-id="element-1901">
                        "Which bus am I on? Am I late?"
                      </h3>
                      <p className="text-sm text-white/80 mt-1" data-id="element-1902">
                        Transportation is the biggest operational stress point
                        at destination weddings. DestaPass eliminates the chaos.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-6 md:p-8" data-id="element-1903">
                  <div className="grid sm:grid-cols-2 gap-4" data-id="element-1904">
                    <div className="group bg-gradient-to-br from-purple-50 to-pink-50/50 rounded-2xl p-5 border border-purple-100/50 hover:shadow-md transition-all duration-300" data-id="element-1905">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white mb-3 group-hover:scale-110 transition-transform" data-id="element-1906">
                        <ClockIcon className="h-5 w-5" data-id="element-1907" />
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm mb-1.5" data-id="element-1908">
                        Bus Schedules
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-1909">
                        Transport schedule per event with pickup locations,
                        departure times, and return times.
                      </p>
                    </div>
                    <div className="group bg-gradient-to-br from-purple-50 to-pink-50/50 rounded-2xl p-5 border border-purple-100/50 hover:shadow-md transition-all duration-300" data-id="element-1910">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white mb-3 group-hover:scale-110 transition-transform" data-id="element-1911">
                        <Camera className="h-5 w-5" data-id="element-1912" />
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm mb-1.5" data-id="element-1913">
                        Pickup Point Photos
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-1914">
                        "Meet at the lobby fountain" with actual photos — no
                        guessing where to stand.
                      </p>
                    </div>
                    <div className="group bg-gradient-to-br from-purple-50 to-pink-50/50 rounded-2xl p-5 border border-purple-100/50 hover:shadow-md transition-all duration-300" data-id="element-1915">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white mb-3 group-hover:scale-110 transition-transform" data-id="element-1916">
                        <Bell className="h-5 w-5" data-id="element-1917" />
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm mb-1.5" data-id="element-1918">
                        Live Status Updates
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-1919">
                        Bus left, bus delayed, or "your shuttle is 5 mins away"
                        — real-time so no one is stranded.
                      </p>
                    </div>
                    <div className="group bg-gradient-to-br from-purple-50 to-pink-50/50 rounded-2xl p-5 border border-purple-100/50 hover:shadow-md transition-all duration-300" data-id="element-1920">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white mb-3 group-hover:scale-110 transition-transform" data-id="element-1921">
                        <AlertCircleIcon className="h-5 w-5" data-id="element-1922" />
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm mb-1.5" data-id="element-1923">
                        Missed the Bus?
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-1924">
                        One-tap help button with taxi/Uber backup instructions
                        and concierge contact.
                      </p>
                    </div>
                  </div>
                </div>
              </div>}

            {activeJob === 2 && <div className="bg-white rounded-3xl border border-purple-100/80 shadow-xl shadow-purple-500/5 overflow-hidden" data-id="element-1925">
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6 md:p-8" data-id="element-1926">
                  <div className="flex items-start gap-4" data-id="element-1927">
                    <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center text-white font-bold text-xl flex-shrink-0 border border-white/30" data-id="element-1928">
                      3
                    </div>
                    <div data-id="element-1929">
                      <h3 className="text-xl md:text-2xl font-bold text-white" data-id="element-1930">
                        Culturally Intelligent, Not Generic
                      </h3>
                      <p className="text-sm text-white/80 mt-1" data-id="element-1931">
                        This is where DestaPass shines — reducing anxiety for
                        non-Indian spouses, second-gen guests, and elders alike.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-6 md:p-8" data-id="element-1932">
                  <div className="grid sm:grid-cols-2 gap-4" data-id="element-1933">
                    <div className="group bg-gradient-to-br from-purple-50 to-pink-50/50 rounded-2xl p-5 border border-purple-100/50 hover:shadow-md transition-all duration-300" data-id="element-1934">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white mb-3 group-hover:scale-110 transition-transform" data-id="element-1935">
                        <ShirtIcon className="h-5 w-5" data-id="element-1936" />
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm mb-1.5" data-id="element-1937">
                        Outfit Guidance
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-1938">
                        "Bright colors encouraged" · "No black for Anand Karaj"
                        · "Beach-friendly footwear" — per event, no guessing.
                      </p>
                    </div>
                    <div className="group bg-gradient-to-br from-purple-50 to-pink-50/50 rounded-2xl p-5 border border-purple-100/50 hover:shadow-md transition-all duration-300" data-id="element-1939">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white mb-3 group-hover:scale-110 transition-transform" data-id="element-1940">
                        <BookOpenIcon className="h-5 w-5" data-id="element-1941" />
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm mb-1.5" data-id="element-1942">
                        Cultural Notes
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-1943">
                        What happens at each ceremony, when to sit or stand,
                        when shoes come off — so every guest feels confident.
                      </p>
                    </div>
                    <div className="group bg-gradient-to-br from-purple-50 to-pink-50/50 rounded-2xl p-5 border border-purple-100/50 hover:shadow-md transition-all duration-300" data-id="element-1944">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white mb-3 group-hover:scale-110 transition-transform" data-id="element-1945">
                        <SunIcon className="h-5 w-5" data-id="element-1946" />
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm mb-1.5" data-id="element-1947">
                        Weather & Comfort
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-1948">
                        "It will be hot — light fabrics recommended" and
                        practical tips for outdoor ceremonies.
                      </p>
                    </div>
                    <div className="group bg-gradient-to-br from-purple-50 to-pink-50/50 rounded-2xl p-5 border border-purple-100/50 hover:shadow-md transition-all duration-300" data-id="element-1949">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white mb-3 group-hover:scale-110 transition-transform" data-id="element-1950">
                        <SmileIcon className="h-5 w-5" data-id="element-1951" />
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm mb-1.5" data-id="element-1952">
                        Everyone Feels Welcome
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-1953">
                        Designed for mixed families — non-Indian partners,
                        elders, and kids all get the context they need.
                      </p>
                    </div>
                  </div>
                </div>
              </div>}

            {activeJob === 3 && <div className="bg-white rounded-3xl border border-purple-100/80 shadow-xl shadow-purple-500/5 overflow-hidden" data-id="element-1954">
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6 md:p-8" data-id="element-1955">
                  <div className="flex items-start gap-4" data-id="element-1956">
                    <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center text-white font-bold text-xl flex-shrink-0 border border-white/30" data-id="element-1957">
                      4
                    </div>
                    <div data-id="element-1958">
                      <h3 className="text-xl md:text-2xl font-bold text-white" data-id="element-1959">
                        Your On-Site Concierge
                      </h3>
                      <p className="text-sm text-white/80 mt-1" data-id="element-1960">
                        Once guests arrive, they stop thinking like travelers
                        and start thinking like residents. No one wants to
                        scroll emails in a sherwani.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-6 md:p-8" data-id="element-1961">
                  <div className="grid sm:grid-cols-2 gap-4" data-id="element-1962">
                    <div className="group bg-gradient-to-br from-purple-50 to-pink-50/50 rounded-2xl p-5 border border-purple-100/50 hover:shadow-md transition-all duration-300" data-id="element-1963">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white mb-3 group-hover:scale-110 transition-transform" data-id="element-1964">
                        <MapIcon className="h-5 w-5" data-id="element-1965" />
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm mb-1.5" data-id="element-1966">
                        Property Map
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-1967">
                        Event venues, breakfast spots, pools, spa, gym, kids
                        areas — everything at a glance.
                      </p>
                    </div>
                    <div className="group bg-gradient-to-br from-purple-50 to-pink-50/50 rounded-2xl p-5 border border-purple-100/50 hover:shadow-md transition-all duration-300" data-id="element-1968">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white mb-3 group-hover:scale-110 transition-transform" data-id="element-1969">
                        <PhoneIcon className="h-5 w-5" data-id="element-1970" />
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm mb-1.5" data-id="element-1971">
                        Emergency & Help
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-1972">
                        Wedding concierge contact, medical/pharmacy info, lost &
                        found — instant access when it matters.
                      </p>
                    </div>
                    <div className="group bg-gradient-to-br from-purple-50 to-pink-50/50 rounded-2xl p-5 border border-purple-100/50 hover:shadow-md transition-all duration-300" data-id="element-1973">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white mb-3 group-hover:scale-110 transition-transform" data-id="element-1974">
                        <MapPinIcon className="h-5 w-5" data-id="element-1975" />
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm mb-1.5" data-id="element-1976">
                        Key Locations
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-1977">
                        Where's breakfast? Where's the ceremony? Where do kids
                        go? — answered before anyone asks.
                      </p>
                    </div>
                    <div className="group bg-gradient-to-br from-purple-50 to-pink-50/50 rounded-2xl p-5 border border-purple-100/50 hover:shadow-md transition-all duration-300" data-id="element-1978">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white mb-3 group-hover:scale-110 transition-transform" data-id="element-1979">
                        <GlobeIcon className="h-5 w-5" data-id="element-1980" />
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm mb-1.5" data-id="element-1981">
                        Food & Dietary Info
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-1982">
                        Event menus with Veg / Jain / Halal indicators and
                        allergy contact info.
                      </p>
                    </div>
                  </div>
                </div>
              </div>}

            {activeJob === 4 && <div className="bg-white rounded-3xl border border-purple-100/80 shadow-xl shadow-purple-500/5 overflow-hidden" data-id="element-1983">
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6 md:p-8" data-id="element-1984">
                  <div className="flex items-start gap-4" data-id="element-1985">
                    <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center text-white font-bold text-xl flex-shrink-0 border border-white/30" data-id="element-1986">
                      5
                    </div>
                    <div data-id="element-1987">
                      <h3 className="text-xl md:text-2xl font-bold text-white" data-id="element-1988">
                        Replace WhatsApp Chaos
                      </h3>
                      <p className="text-sm text-white/80 mt-1" data-id="element-1989">
                        WhatsApp groups explode at 150 people. DestaPass gives
                        you structured communication without the noise.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-6 md:p-8" data-id="element-1990">
                  <div className="grid sm:grid-cols-2 gap-4" data-id="element-1991">
                    <div className="group bg-gradient-to-br from-purple-50 to-pink-50/50 rounded-2xl p-5 border border-purple-100/50 hover:shadow-md transition-all duration-300" data-id="element-1992">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white mb-3 group-hover:scale-110 transition-transform" data-id="element-1993">
                        <MegaphoneIcon className="h-5 w-5" data-id="element-1994" />
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm mb-1.5" data-id="element-1995">
                        Host Announcements
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-1996">
                        "Reception moved indoors" · "Fireworks delayed 15 mins"
                        — broadcast to all guests instantly.
                      </p>
                    </div>
                    <div className="group bg-gradient-to-br from-purple-50 to-pink-50/50 rounded-2xl p-5 border border-purple-100/50 hover:shadow-md transition-all duration-300" data-id="element-1997">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white mb-3 group-hover:scale-110 transition-transform" data-id="element-1998">
                        <HelpCircleIcon className="h-5 w-5" data-id="element-1999" />
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm mb-1.5" data-id="element-2000">
                        FAQ Section
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-2001">
                        "Can I bring kids?" · "Is alcohol served?" · "What
                        currency?" — answered once, available always.
                      </p>
                    </div>
                    <div className="group bg-gradient-to-br from-purple-50 to-pink-50/50 rounded-2xl p-5 border border-purple-100/50 hover:shadow-md transition-all duration-300" data-id="element-2002">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white mb-3 group-hover:scale-110 transition-transform" data-id="element-2003">
                        <UsersIcon className="h-5 w-5" data-id="element-2004" />
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm mb-1.5" data-id="element-2005">
                        Contact Directory
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-2006">
                        Wedding team, transportation lead, hotel desk — one tap
                        to reach the right person.
                      </p>
                    </div>
                    <div className="group bg-gradient-to-br from-purple-50 to-pink-50/50 rounded-2xl p-5 border border-purple-100/50 hover:shadow-md transition-all duration-300" data-id="element-2007">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white mb-3 group-hover:scale-110 transition-transform" data-id="element-2008">
                        <MessageCircleIcon className="h-5 w-5" data-id="element-2009" />
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm mb-1.5" data-id="element-2010">
                        No More Group Chat Spam
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-2011">
                        Important updates don't get buried under 200
                        "congratulations!" messages.
                      </p>
                    </div>
                  </div>
                </div>
              </div>}

            {/* Coming Soon Teaser */}
            <div className="mt-10 bg-white rounded-3xl p-8 border border-purple-100/80 shadow-lg shadow-purple-500/5 relative overflow-hidden" data-id="element-2012">
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl from-pink-100/50 to-transparent rounded-full blur-2xl" data-id="element-2013"></div>
              <div className="relative" data-id="element-2014">
                <div className="flex items-center gap-3 mb-5" data-id="element-2015">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center" data-id="element-2016">
                    <StarIcon className="h-5 w-5 text-white" data-id="element-2017" />
                  </div>
                  <div data-id="element-2018">
                    <h4 className="font-bold text-gray-900" data-id="element-2019">
                      Also Included — Delighter Features
                    </h4>
                    <p className="text-xs text-gray-500" data-id="element-2020">
                      Built-in extras that elevate the guest experience
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2" data-id="element-2021">
                  {[{
                  label: 'Shared photo & video album',
                  emoji: '📸'
                }, {
                  label: 'Interactive polls & RSVP',
                  emoji: '📊'
                }, {
                  label: 'VIP upgrade marketplace',
                  emoji: '✨'
                }, {
                  label: 'Custom guest profiles',
                  emoji: '👤'
                }].map((feature, idx) => <span key={idx} className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-purple-50 to-pink-50 rounded-full text-xs font-medium text-purple-700 border border-purple-200/50" data-id="element-2022">
                      <span data-id="element-2023">{feature.emoji}</span>
                      {feature.label}
                    </span>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is DestaPass */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-white to-purple-50 relative z-10" data-id="element-2024">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2025">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2026">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-2027">
              What's Included in Your DestaPass
            </h2>
            <p className="text-lg text-gray-600" data-id="element-2028">
              Everything you need to bring your families together before the big
              day
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto" data-id="element-2029">
            {features.map((feature, index) => <div key={index} className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100" data-id="element-2030">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white mb-4" data-id="element-2031">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-2032">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-2033">{feature.description}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* What You'll Need Ready */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-purple-50 to-white relative z-10" data-id="element-2034">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2035">
          <div className="max-w-4xl mx-auto" data-id="element-2036">
            <div className="text-center mb-12" data-id="element-2037">
              <div className="inline-flex items-center px-4 py-2 bg-purple-100 rounded-full text-purple-700 text-sm font-medium mb-4" data-id="element-2038">
                <ClipboardListIcon className="h-4 w-4 mr-2" data-id="element-2039" />
                Preparation Checklist
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-2040">
                What to Have Ready for Your Call
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-2041">
                During our activation call, we'll record your welcome videos
                together and set up your app. Here's what you'll need:
              </p>
            </div>

            <div className="bg-white rounded-3xl shadow-xl p-8 border border-purple-100" data-id="element-2042">
              <div className="space-y-4" data-id="element-2043">
                {preparationChecklist.map((item, index) => <div key={index} className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 hover:bg-purple-50 transition-colors" data-id="element-2044">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${item.required ? 'bg-purple-600 text-white' : 'bg-gray-300 text-gray-600'}`} data-id="element-2045">
                      {item.required ? <CheckIcon className="h-5 w-5" data-id="element-2046" /> : <span className="text-xs font-medium" data-id="element-2047">+</span>}
                    </div>
                    <div className="flex-1" data-id="element-2048">
                      <div className="flex items-center gap-2" data-id="element-2049">
                        <h4 className="font-semibold text-gray-900" data-id="element-2050">
                          {item.title}
                        </h4>
                        {item.required && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full" data-id="element-2051">
                            Required
                          </span>}
                        {!item.required && <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full" data-id="element-2052">
                            Optional
                          </span>}
                      </div>
                      <p className="text-sm text-gray-600 mt-1" data-id="element-2053">
                        {item.description}
                      </p>
                    </div>
                  </div>)}
              </div>

              <div className="mt-8 p-6 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl text-white" data-id="element-2054">
                <div className="flex items-start gap-4" data-id="element-2055">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-2056">
                    <VideoIcon className="h-6 w-6" data-id="element-2057" />
                  </div>
                  <div data-id="element-2058">
                    <h4 className="font-bold text-lg mb-2" data-id="element-2059">
                      About the Welcome Videos
                    </h4>
                    <p className="text-white/90 text-sm leading-relaxed" data-id="element-2060">
                      Don't worry about being perfect! We'll guide you through
                      recording during the call. You can use your phone camera,
                      and we'll help with lighting and framing. The videos are
                      short (30-60 seconds) and can be re-recorded if needed.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 text-center" data-id="element-2061">
                <a href="https://form.typeform.com/to/BOaT9mnh" target="_blank" rel="noopener noreferrer" data-id="element-2062">
                  <Button variant="primary" size="lg" className="!bg-gradient-to-r !from-pink-500 !to-purple-500 !text-white hover:!from-pink-600 hover:!to-purple-600 border-0 shadow-xl font-semibold" data-id="element-2063">
                    Upload Your Photos and Videos Here
                    <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-2064" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sample Scripts */}
      <section className="py-16 md:py-20 bg-white relative z-10" data-id="element-2065">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2066">
          <div className="max-w-4xl mx-auto" data-id="element-2067">
            <div className="text-center mb-12" data-id="element-2068">
              <div className="inline-flex items-center px-4 py-2 bg-pink-100 rounded-full text-pink-700 text-sm font-medium mb-4" data-id="element-2069">
                <MessageSquareIcon className="h-4 w-4 mr-2" data-id="element-2070" />
                Sample Scripts
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-2071">
                Welcome Video Scripts
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-2072">
                Use these as inspiration or read them directly—customize with
                your names and details!
              </p>
            </div>

            <div className="space-y-4" data-id="element-2073">
              {Object.entries(scripts).map(([key, scriptData]) => <Card key={key} className="border-2 border-purple-100 overflow-hidden" data-id="element-2074">
                  <button onClick={() => setExpandedScript(expandedScript === key ? null : key)} className="w-full p-6 flex items-center justify-between text-left hover:bg-purple-50 transition-colors" data-id="element-2075">
                    <div className="flex items-center gap-4" data-id="element-2076">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white" data-id="element-2077">
                        <PlayCircleIcon className="h-6 w-6" data-id="element-2078" />
                      </div>
                      <div data-id="element-2079">
                        <h3 className="text-xl font-bold text-gray-900" data-id="element-2080">
                          {scriptData.title}
                        </h3>
                        <p className="text-sm text-gray-600" data-id="element-2081">
                          Duration: {scriptData.duration} •{' '}
                          {scriptData.options.length} script options
                        </p>
                      </div>
                    </div>
                    {expandedScript === key ? <ChevronUpIcon className="h-6 w-6 text-gray-400" data-id="element-2082" /> : <ChevronDownIcon className="h-6 w-6 text-gray-400" data-id="element-2083" />}
                  </button>

                  {expandedScript === key && <CardBody className="pt-0 px-6 pb-6" data-id="element-2084">
                      <div className="space-y-6" data-id="element-2085">
                        {scriptData.options.map((option, index) => <div key={index} className="bg-gray-50 rounded-xl p-6" data-id="element-2086">
                            <div className="flex items-center justify-between mb-4" data-id="element-2087">
                              <h4 className="font-semibold text-purple-700" data-id="element-2088">
                                Option {index + 1}: {option.name}
                              </h4>
                              <button onClick={() => copyToClipboard(option.script, `${key}-${index}`)} className="flex items-center gap-2 text-sm text-purple-600 hover:text-purple-800 transition-colors" data-id="element-2089">
                                <CopyIcon className="h-4 w-4" data-id="element-2090" />
                                {copiedScript === `${key}-${index}` ? 'Copied!' : 'Copy'}
                              </button>
                            </div>
                            <div className="bg-white rounded-lg p-4 border border-gray-200" data-id="element-2091">
                              <pre className="whitespace-pre-wrap text-gray-700 text-sm font-sans leading-relaxed" data-id="element-2092">
                                {option.script}
                              </pre>
                            </div>
                          </div>)}
                      </div>
                    </CardBody>}
                </Card>)}
            </div>

            <div className="mt-8 text-center" data-id="element-2093">
              <p className="text-gray-600 mb-2" data-id="element-2094">
                <strong data-id="element-2095">Pro tip:</strong> Replace [bracketed text] with your
                actual names and details
              </p>
              <p className="text-sm text-gray-500" data-id="element-2096">
                Feel free to mix and match or write your own—these are just
                starting points!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-purple-50 to-pink-50 relative z-10" data-id="element-2097">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2098">
          <div className="max-w-4xl mx-auto" data-id="element-2099">
            <div className="text-center mb-12" data-id="element-2100">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-2101">
                How Activation Works
              </h2>
              <p className="text-lg text-gray-600" data-id="element-2102">
                Three simple steps to get your DestaPass live
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8" data-id="element-2103">
              <div className="text-center" data-id="element-2104">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4 shadow-lg" data-id="element-2105">
                  1
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-2106">
                  Schedule Your Call
                </h3>
                <p className="text-gray-600" data-id="element-2107">
                  Book a 30-minute activation call at a time that works for you
                  and your partner
                </p>
              </div>

              <div className="text-center" data-id="element-2108">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4 shadow-lg" data-id="element-2109">
                  2
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-2110">
                  Record Together
                </h3>
                <p className="text-gray-600" data-id="element-2111">
                  We'll guide you through recording your welcome videos and
                  setting up your app
                </p>
              </div>

              <div className="text-center" data-id="element-2112">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4 shadow-lg" data-id="element-2113">
                  3
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-2114">
                  Invite Your Guests
                </h3>
                <p className="text-gray-600" data-id="element-2115">
                  We'll send invitations to your guest list and your DestaPass
                  goes live!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-28 relative overflow-hidden" data-id="element-2116">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-600 via-pink-600 to-purple-700" data-id="element-2117"></div>
        <div className="absolute inset-0 opacity-20" data-id="element-2118">
          <div className="absolute top-10 right-10 w-64 h-64 bg-white rounded-full blur-3xl" data-id="element-2119"></div>
          <div className="absolute bottom-10 left-10 w-80 h-80 bg-pink-300 rounded-full blur-3xl" data-id="element-2120"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-2121">
          <div className="max-w-3xl mx-auto text-center text-white" data-id="element-2122">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-2123">
              <CalendarIcon className="h-10 w-10" data-id="element-2124" />
            </div>

            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-2125">
              Ready to Activate Your DestaPass?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto" data-id="element-2126">
              Schedule your activation call and we'll get your wedding app live.
              Make sure you have your welcome video scripts ready!
            </p>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-white/20 max-w-md mx-auto" data-id="element-2127">
              <h4 className="font-semibold mb-3" data-id="element-2128">
                Before you book, make sure you have:
              </h4>
              <ul className="text-left text-sm space-y-2 text-white/90" data-id="element-2129">
                <li className="flex items-center gap-2" data-id="element-2130">
                  <CheckIcon className="h-4 w-4 text-green-400 flex-shrink-0" data-id="element-2131" />
                  Your partner available for the call
                </li>
                <li className="flex items-center gap-2" data-id="element-2132">
                  <CheckIcon className="h-4 w-4 text-green-400 flex-shrink-0" data-id="element-2133" />
                  Welcome video scripts prepared
                </li>
                <li className="flex items-center gap-2" data-id="element-2134">
                  <CheckIcon className="h-4 w-4 text-green-400 flex-shrink-0" data-id="element-2135" />
                  Good lighting and quiet space
                </li>
                <li className="flex items-center gap-2" data-id="element-2136">
                  <CheckIcon className="h-4 w-4 text-green-400 flex-shrink-0" data-id="element-2137" />
                  Guest list with email addresses
                </li>
              </ul>
            </div>

            <a href={calendlyUrl} target="_blank" rel="noopener noreferrer" data-id="element-2138">
              <Button variant="primary" size="lg" className="!bg-gradient-to-r !from-pink-500 !to-purple-500 !text-white hover:!from-pink-600 hover:!to-purple-600 border-0 shadow-xl font-semibold" data-id="element-2139">
                Schedule Your Activation Call
                <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-2140" />
              </Button>
            </a>

            <p className="text-sm text-white/70 mt-6" data-id="element-2141">
              Questions? Email us at{' '}
              <a href="mailto:Destapass@destameet.com" className="underline hover:text-white" data-id="element-2142">
                Destapass@destameet.com
              </a>
            </p>
          </div>
        </div>
      </section>
    </div>;
}